import { SUPPORTED_DOCUMENT_EXTENSIONS } from "./upload-file-types";

export const MAX_UPLOAD_FILES = 10;
export const MAX_UPLOAD_FILE_SIZE_MB = 20;
export const MAX_UPLOAD_FILE_SIZE = MAX_UPLOAD_FILE_SIZE_MB * 1024 * 1024;

export type UploadFileProblem = "tooLarge" | "unsupportedType";

export const getFileExtension = (fileName: string) => {
  const dotIndex = fileName.lastIndexOf(".");
  return dotIndex > 0 ? fileName.slice(dotIndex).toLowerCase() : "";
};

// The native picker filters by `accept`, but drag-and-drop does not, so every
// file is checked here regardless of how it arrived.
export const validateUploadFile = (
  file: Pick<File, "name" | "size">,
): UploadFileProblem | null => {
  const extension = getFileExtension(file.name);
  if (
    !(SUPPORTED_DOCUMENT_EXTENSIONS as readonly string[]).includes(extension)
  ) {
    return "unsupportedType";
  }
  if (file.size > MAX_UPLOAD_FILE_SIZE) {
    return "tooLarge";
  }
  return null;
};

// The single-file dialog asked for a title; with many files the file name is
// the only sensible default.
export const titleFromFileName = (fileName: string) => {
  const dotIndex = fileName.lastIndexOf(".");
  const base = dotIndex > 0 ? fileName.slice(0, dotIndex) : fileName;
  return base.trim() || fileName;
};

export const MIN_DOCUMENT_YEAR = 1000;
export const getMaxDocumentYear = () => new Date().getFullYear() + 1;

/** Per-file details edited before upload. `year` stays a string while typed. */
export type DocumentDetails = {
  title: string;
  author: string;
  year: string;
  tags: string[];
};

export const createDocumentDetails = (fileName: string): DocumentDetails => ({
  title: titleFromFileName(fileName),
  author: "",
  year: "",
  tags: [],
});

/** True when anything beyond the prefilled title was added. */
export const hasExtraDetails = (details: DocumentDetails) =>
  Boolean(details.author.trim() || details.year.trim() || details.tags.length);

export type YearProblem = "notInteger" | "tooSmall" | "tooLarge";

export const validateDocumentYear = (year: string): YearProblem | null => {
  if (!year.trim()) return null;
  const numericYear = Number(year);
  if (!Number.isInteger(numericYear)) return "notInteger";
  if (numericYear < MIN_DOCUMENT_YEAR) return "tooSmall";
  if (numericYear > getMaxDocumentYear()) return "tooLarge";
  return null;
};

export const isDocumentDetailsValid = (details: DocumentDetails) =>
  Boolean(details.title.trim()) && !validateDocumentYear(details.year);

/** Metadata in the shape `documentsApi.uploadDocument` sends, or undefined. */
export const toUploadMetadata = (details: DocumentDetails) => {
  const metadata: { author?: string; year?: number; tags?: string[] } = {};
  if (details.author.trim()) metadata.author = details.author.trim();
  if (details.year.trim()) metadata.year = Number(details.year);
  if (details.tags.length) metadata.tags = details.tags;
  return Object.keys(metadata).length > 0 ? metadata : undefined;
};

export const getFileIdentity = (
  file: Pick<File, "name" | "size" | "lastModified">,
) => `${file.name}:${file.size}:${file.lastModified}`;

export const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Runs `worker` over `items` with at most `limit` in flight at once.
 * Workers are expected to handle their own errors.
 */
export const runWithConcurrency = async <T>(
  items: readonly T[],
  limit: number,
  worker: (item: T) => Promise<void>,
) => {
  let nextIndex = 0;
  const runners = Array.from(
    { length: Math.min(limit, items.length) },
    async () => {
      while (nextIndex < items.length) {
        const item = items[nextIndex];
        nextIndex += 1;
        await worker(item);
      }
    },
  );
  await Promise.all(runners);
};
